<script setup>
import { ref, computed, onMounted } from 'vue';
import segueDashboardAPI from 'dashboard/api/segueDashboard';

// ============================================================================
// v106: RANKING DE VENDAS (pedido do Kairo 06/07) — pódium 1º/2º/3º + lista,
// com a SOMA DO VALOR LÍQUIDO (valor_liberado) dos contratos PAGOS no período
// por agente. Reusa o endpoint contratos_resumo (ContratosResumoService):
// vendedores[].cats['Pagos'] = { qtd, valor }. O balde "CLT IA" (id 1 =
// contratos sem dono humano) fica FORA do ranking — é a IA, não agente.
// Período padrão: MÊS ATUAL.
// ============================================================================

const PERIODOS = [
  { key: 'month', label: 'Mês atual' },
  { key: 'today', label: 'Hoje' },
  { key: '7d', label: '7 dias' },
  { key: '30d', label: '30 dias' },
];

const period = ref('month');
const isLoading = ref(false);
const error = ref('');
const vendedores = ref([]);

const brl = v =>
  (Number(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const iniciais = nome =>
  String(nome || '?')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(p => p.charAt(0).toUpperCase())
    .join('');

const primeiroNome = nome => {
  const p = String(nome || '').trim().split(/\s+/);
  return p.length > 1 ? `${p[0]} ${p[p.length - 1].charAt(0)}.` : p[0] || '—';
};

const load = async () => {
  isLoading.value = true;
  error.value = '';
  try {
    const { data } = await segueDashboardAPI.fetchContratosResumo({
      period: period.value,
    });
    vendedores.value = (data.vendedores || [])
      .filter(v => v.id !== 1) // fora o balde "CLT IA"
      .map(v => ({
        id: v.id,
        nome: v.nome,
        valor: Number(v.cats?.Pagos?.valor) || 0,
        qtd: Number(v.cats?.Pagos?.qtd) || 0,
      }))
      .filter(v => v.qtd > 0)
      .sort((a, b) => b.valor - a.valor);
  } catch (e) {
    error.value = e.response?.data?.error || e.message;
  } finally {
    isLoading.value = false;
  }
};

const selecionar = key => {
  period.value = key;
  load();
};

const top3 = computed(() => vendedores.value.slice(0, 3));
const demais = computed(() => vendedores.value.slice(3));
const maiorValor = computed(() => vendedores.value[0]?.valor || 1);

const tituloPeriodo = computed(() => {
  if (period.value !== 'month') {
    return PERIODOS.find(p => p.key === period.value)?.label || '';
  }
  const agora = new Date();
  const nome = agora.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  return nome.charAt(0).toUpperCase() + nome.slice(1);
});

onMounted(load);
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -->
<template>
  <div class="overflow-auto p-4 w-full h-full bg-n-background">
    <!-- header -->
    <div class="flex flex-wrap gap-3 items-center mb-6">
      <span class="rk-trofeu"><span class="i-lucide-trophy size-5" /></span>
      <div>
        <h1 class="text-xl font-semibold text-n-slate-12">Ranking de Vendas</h1>
        <p class="text-xs text-n-slate-11">
          Contratos pagos · {{ tituloPeriodo }}
        </p>
      </div>
      <span class="flex-1" />
      <div class="flex gap-1.5">
        <button
          v-for="p in PERIODOS"
          :key="p.key"
          type="button"
          class="px-3 py-1.5 text-xs font-semibold rounded-lg border"
          :class="
            period === p.key
              ? 'bg-n-brand text-white border-transparent'
              : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-2'
          "
          @click="selecionar(p.key)"
        >
          {{ p.label }}
        </button>
      </div>
    </div>

    <p v-if="error" class="p-3 mb-4 text-sm rounded-lg border border-n-ruby-7 bg-n-ruby-2 text-n-ruby-11">
      {{ error }}
    </p>

    <div v-if="isLoading" class="py-16 text-center text-n-slate-11">
      <span class="inline-block i-lucide-loader-circle animate-spin size-8" />
      <p class="mt-2 text-sm">Carregando ranking…</p>
    </div>

    <div v-else-if="!vendedores.length" class="py-16 text-center text-n-slate-11">
      <span class="inline-block i-lucide-trophy size-10 opacity-40" />
      <p class="mt-2 text-sm">Nenhum contrato pago no período.</p>
    </div>

    <template v-else>
      <!-- ==================== PÓDIUM ==================== -->
      <div class="rk-podium">
        <!-- 2º lugar -->
        <div v-if="top3[1]" class="rk-coluna">
          <div class="rk-avatar prata">{{ iniciais(top3[1].nome) }}</div>
          <div class="rk-medalha">🥈</div>
          <p class="rk-nome">{{ primeiroNome(top3[1].nome) }}</p>
          <p class="rk-valor tabular-nums">{{ brl(top3[1].valor) }}</p>
          <p class="rk-qtd">{{ top3[1].qtd }} contrato(s)</p>
          <div class="rk-base prata-base">2º</div>
        </div>

        <!-- 1º lugar -->
        <div v-if="top3[0]" class="rk-coluna campeao">
          <div class="rk-coroa">👑</div>
          <div class="rk-avatar ouro">{{ iniciais(top3[0].nome) }}</div>
          <div class="rk-medalha">🥇</div>
          <p class="rk-nome destaque">{{ primeiroNome(top3[0].nome) }}</p>
          <p class="rk-valor grande tabular-nums">{{ brl(top3[0].valor) }}</p>
          <p class="rk-qtd">{{ top3[0].qtd }} contrato(s)</p>
          <div class="rk-base ouro-base">1º</div>
        </div>

        <!-- 3º lugar -->
        <div v-if="top3[2]" class="rk-coluna">
          <div class="rk-avatar bronze">{{ iniciais(top3[2].nome) }}</div>
          <div class="rk-medalha">🥉</div>
          <p class="rk-nome">{{ primeiroNome(top3[2].nome) }}</p>
          <p class="rk-valor tabular-nums">{{ brl(top3[2].valor) }}</p>
          <p class="rk-qtd">{{ top3[2].qtd }} contrato(s)</p>
          <div class="rk-base bronze-base">3º</div>
        </div>
      </div>

      <!-- ==================== LISTA (4º em diante) ==================== -->
      <div v-if="demais.length" class="mx-auto mt-8 max-w-3xl">
        <h2 class="mb-3 text-sm font-semibold text-n-slate-11">
          Quem mais vendeu
        </h2>
        <div class="flex flex-col gap-2">
          <div
            v-for="(v, i) in demais"
            :key="v.id"
            class="rk-linha"
          >
            <span class="rk-pos tabular-nums">{{ i + 4 }}º</span>
            <span class="rk-mini-avatar">{{ iniciais(v.nome) }}</span>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-semibold truncate text-n-slate-12">{{ v.nome }}</p>
              <div class="rk-barra-fundo">
                <div
                  class="rk-barra"
                  :style="{ width: `${Math.max(4, (v.valor / maiorValor) * 100)}%` }"
                />
              </div>
            </div>
            <div class="text-right shrink-0">
              <p class="text-sm font-bold tabular-nums text-n-slate-12">{{ brl(v.valor) }}</p>
              <p class="text-[11px] text-n-slate-11">{{ v.qtd }} contrato(s)</p>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
/* ============================================================================
   v106 — Ranking de vendas: pódium ouro/prata/bronze + lista com barras.
   Escopado — não vaza pro resto do sistema.
   ========================================================================= */
.rk-trofeu {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: linear-gradient(135deg, #f7c948, #e8a413);
  color: #3a2a00;
  box-shadow: 0 4px 14px rgba(247, 201, 72, 0.35);
}

/* ---- pódium ---- */
.rk-podium {
  display: flex;
  gap: 16px;
  align-items: flex-end;
  justify-content: center;
  margin: 8px auto 0;
  max-width: 760px;
}
.rk-coluna {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  max-width: 220px;
  padding: 16px 12px 0;
  border-radius: 16px 16px 0 0;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.04), transparent);
}
.rk-coluna.campeao {
  max-width: 250px;
}
.rk-coroa {
  font-size: 26px;
  margin-bottom: 2px;
  animation: rk-flutua 2.4s ease-in-out infinite;
}
@keyframes rk-flutua {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
}
.rk-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  font-weight: 800;
  font-size: 20px;
  color: #1a1300;
}
.campeao .rk-avatar {
  width: 80px;
  height: 80px;
  font-size: 26px;
}
.rk-avatar.ouro {
  background: linear-gradient(135deg, #ffe066, #f0a500);
  box-shadow: 0 0 0 4px rgba(240, 165, 0, 0.35), 0 8px 24px rgba(240, 165, 0, 0.35);
}
.rk-avatar.prata {
  background: linear-gradient(135deg, #e8edf2, #9aa7b5);
  box-shadow: 0 0 0 3px rgba(154, 167, 181, 0.35);
  color: #1d242c;
}
.rk-avatar.bronze {
  background: linear-gradient(135deg, #e3a878, #b06a35);
  box-shadow: 0 0 0 3px rgba(176, 106, 53, 0.35);
  color: #2a1503;
}
.rk-medalha {
  font-size: 22px;
  margin-top: 6px;
}
.rk-nome {
  margin-top: 2px;
  font-size: 14px;
  font-weight: 700;
  color: var(--slate-12, #e6e9ef);
  text-align: center;
}
.rk-nome.destaque {
  font-size: 16px;
}
.rk-valor {
  font-size: 16px;
  font-weight: 800;
  color: #1ee7ca;
}
.rk-valor.grande {
  font-size: 20px;
}
.rk-qtd {
  font-size: 11px;
  color: #9ca3af;
  margin-bottom: 10px;
}
.rk-base {
  width: 100%;
  border-radius: 12px 12px 0 0;
  text-align: center;
  font-weight: 800;
  color: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
}
.ouro-base {
  height: 120px;
  background: linear-gradient(180deg, #f7c948, #b97f0a);
  box-shadow: 0 -6px 24px rgba(247, 201, 72, 0.25);
}
.prata-base {
  height: 84px;
  background: linear-gradient(180deg, #cfd6de, #7f8b99);
}
.bronze-base {
  height: 62px;
  background: linear-gradient(180deg, #d99a67, #8a5426);
}

/* ---- lista ---- */
.rk-linha {
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 10px 14px;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.03);
}
.rk-pos {
  width: 34px;
  font-size: 14px;
  font-weight: 800;
  color: #9ca3af;
  text-align: center;
  flex-shrink: 0;
}
.rk-mini-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  font-size: 12px;
  font-weight: 800;
  background: linear-gradient(135deg, #2dd4bf, #0e7490);
  color: #06251f;
  flex-shrink: 0;
}
.rk-barra-fundo {
  margin-top: 4px;
  height: 6px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.06);
  overflow: hidden;
}
.rk-barra {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #1ee7ca, #0ea5e9);
}

@media (max-width: 640px) {
  .rk-podium {
    gap: 8px;
  }
  .rk-valor {
    font-size: 13px;
  }
  .rk-valor.grande {
    font-size: 15px;
  }
}
</style>
